import { LowestPriceRequest } from "../store/productPriceDetails.types";
import { ProductPriceDetails } from "../types/productPriceDetails";
import { retryAsync } from "../util/retry-async";

// Base URL for the price platform service
const pricePlatformBaseUrl = "https://api.price-platform-service-cdn.pai.vpsvc.com/v2/pricing";

// Function to fetch product price details from the API
const fetchProductPriceDetails = async (
  request: LowestPriceRequest,
): Promise<Record<string, ProductPriceDetails>> => {
  // Make API call to fetch product price details using mpvIds
  // Quantities available in quantities array is passed to the API call to fetch the prices of each quantity in one go itself in case of multiple quantity options available for a product
  const url = `${pricePlatformBaseUrl}/tenant/${request.tenant}/locale/${request.locale}/lowest-price?requestor=${request.requestor}&pageId=${request.pageId}`;

  const retryCondition = (error: unknown): boolean => {
    // Retry on server errors (5xx) or network errors
    return typeof error === "object" && error !== null && "status" in error &&
      (error as { status: number }).status >= 500 && (error as { status: number }).status < 600;
  };

  const response = await retryAsync(async () => {
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
    });

    if (!res.ok) {
      const error = new Error(res.statusText) as Error & { status?: number };
      error.status = res.status; // Attach status code to the error
      throw error;
    }

    return res;
  }, { retryCondition });

  const responseData: ProductPriceDetails[] = await response.json();

  const priceDetails: Record<string, ProductPriceDetails> = {};
  responseData.forEach(product => {
    const { id, productId, mpvId, prices, quantityMetaData, displayStrategy, priceDisplayGroup } = product;
    priceDetails[id] = {
      id,
      ...(productId && { productId }),
      ...(mpvId && { mpvId }),
      prices: prices,
      quantityMetaData,
      isError: !prices,
      displayStrategy,
      priceDisplayGroup,
    };
  });

  return priceDetails;
};

export { fetchProductPriceDetails };
